  import { Component, input, output, computed, signal, ChangeDetectionStrategy } from '@angular/core';
  import { CommonModule } from '@angular/common';
  import { 
    addMonths, subMonths, format, isSameDay, isWeekend, startOfMonth, 
    endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isBefore, 
    startOfDay, isWithinInterval 
  } from 'date-fns';
  import { Timeslot } from '../../models/timeslot-model';
  import { AvailableDatesDto } from '../../models/booked-dates-model';

  const BG_MONTHS: Record<string, string> = {
    'January': 'Януари', 'February': 'Февруари', 'March': 'Март', 'April': 'Април',
    'May': 'Май', 'June': 'Юни', 'July': 'Юли', 'August': 'Август',
    'September': 'Септември', 'October': 'Октомври', 'November': 'Ноември', 'December': 'Декември'
  };

  @Component({
    selector: 'app-date-time-picker',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
      <div class="space-y-4">
        
        @if (!selectedDate()) {
          <div class="bg-[#BF7A7A]/10 border border-[#BF7A7A]/30 rounded-xl p-4 flex items-start gap-3">
            <div class="w-6 h-6 bg-[#BF7A7A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <lucide-icon name="info" class="w-4 h-4 text-white"></lucide-icon>
            </div>
            <div>
              <p class="font-semibold text-[#121219] mb-1">Изберете дата</p>
              <p class="text-sm text-[#67686C]">Изберете работен ден от календара. Уикендите и официалните празници не са налични.</p>
            </div>
          </div>
        }

        <div class="grid md:grid-cols-2 gap-6 md:gap-8">
          
          <div class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-full flex flex-col"
              (touchstart)="onTouchStart($event)" 
              (touchend)="onTouchEnd($event)"> <div class="flex items-center gap-2 mb-6">
              <lucide-icon name="calendar" class="w-5 h-5 text-[#BF7A7A]"></lucide-icon>
              <h3 class="font-semibold text-[#121219]">Изберете дата</h3>
              
              @if (!selectedDate()) {
                <span class="text-xs bg-[#BF7A7A] text-white px-2 py-0.5 rounded-full ml-auto">Задължително</span>
              }
            </div>

            <div class="flex items-center justify-between mb-6 px-2">
              <button (click)="changeMonth(-1)" 
                      class="p-2 hover:bg-slate-50 rounded-full text-slate-400 hover:text-[#121219] transition-colors">
                <lucide-icon name="chevron-left" class="w-5 h-5"></lucide-icon>
              </button>
              
              <span class="font-semibold text-[#121219] text-lg capitalize">
                {{ getBgMonthYear() }}
              </span>
              
              <button (click)="changeMonth(1)" 
                      class="p-2 hover:bg-slate-50 rounded-full text-slate-400 hover:text-[#121219] transition-colors">
                <lucide-icon name="chevron-right" class="w-5 h-5"></lucide-icon>
              </button>
            </div>

            <div class="grid grid-cols-7 mb-2">
              @for (day of weekDays; track day) {
                <div class="text-center text-xs font-medium text-slate-400 uppercase py-2">
                  {{ day }}
                </div>
              }
            </div>

            <div class="grid grid-cols-7 gap-1 md:gap-2 flex-1 content-start">
              @for (day of calendarDays(); track day) {
                <div class="relative aspect-square">
                  <button
                    type="button"
                    (click)="onDayClick(day)"
                    [disabled]="isDisabled(day) && !isDateFullyBooked(day)" 
                    class="w-full h-full rounded-xl flex flex-col items-center justify-center text-sm transition-all duration-200 relative"
                    [ngClass]="getDayClasses(day)">
                    
                    <span class="relative z-10">{{ day | date:'d' }}</span>
                    
                    @if (!isSelected(day)) {
                      @if (isDateFullyBooked(day)) {
                        <div class="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-red-400"></div>
                      } @else if (!isDisabled(day)) {
                        <div class="absolute bottom-1.5 w-1.5 h-1.5 rounded-full bg-green-400"></div>
                      }
                    }
                  </button>
                </div>
              }
            </div>
            
            <div class="mt-4 flex justify-center gap-4 text-xs text-slate-500 border-t pt-4 border-slate-100">
              <div class="flex items-center gap-1"><div class="w-2 h-2 rounded-full bg-green-400"></div> Свободно</div>
              <div class="flex items-center gap-1"><div class="w-2 h-2 rounded-full bg-red-400"></div> Заето</div>
            </div>
          </div>

          <div class="bg-white rounded-3xl p-6 shadow-sm border border-slate-100 h-full flex flex-col">
            <div class="flex items-center gap-2 mb-6">
              <lucide-icon name="clock" class="w-5 h-5 text-[#BF7A7A]"></lucide-icon>
              <h3 class="font-semibold text-[#121219]">Изберете час</h3>

              @if (selectedDate() && !selectedTimeslotId()) {
                <span class="text-xs bg-[#BF7A7A] text-white px-2 py-0.5 rounded-full ml-auto">Задължително</span>
              }
            </div>

            @if (!selectedDate()) {
              <div class="flex-1 flex flex-col items-center justify-center text-center p-4">
                <div class="w-16 h-16 bg-[#BF7A7A]/10 rounded-full flex items-center justify-center mb-4">
                  <lucide-icon name="calendar" class="w-8 h-8 text-[#BF7A7A]"></lucide-icon>
                </div>
                <p class="font-medium text-[#121219]">Първо изберете дата</p>
                <p class="text-sm text-slate-500 mt-1">Свободните часове ще се появят тук</p>
              </div>
            } @else {
              <div class="grid grid-cols-2 gap-3 content-start">
                @for (slot of timeslots(); track slot.id) {
                  <button 
                    type="button"
                    (click)="isTimeAvailable(slot.id) && onTimeSelect(slot.id)"
                    [disabled]="!isTimeAvailable(slot.id)"
                    class="py-3 px-4 rounded-xl border text-sm font-medium transition-all duration-200 relative"
                    [ngClass]="getTimeSlotClass(slot.id)">
                    
                    {{ slot.name }}
                    
                    @if (!isTimeAvailable(slot.id)) {
                      <span class="absolute top-1 right-1 text-[10px]">🔒</span>
                    }
                  </button>
                }
              </div>
              
              <div class="mt-auto pt-6 text-center">
                <p class="text-xs text-slate-400">
                  Всички часове са в местно време
                </p>
              </div>
            }
          </div>

        </div>
      </div>
    `
  })
  export class DateTimePickerComponent {
    selectedDate = input<string | null>(null);
    selectedTimeslotId = input<number | null>(null);
    
    timeslots = input<Timeslot[]>([]);
    bookedDates = input<AvailableDatesDto[]>([]); 
    vacations = input<any[]>([]);

    dateChange = output<Date>();
    monthChange = output<Date>();
    timeChange = output<number>();

    currentViewDate = signal(new Date());
    weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];
    today = startOfDay(new Date());

    calendarDays = computed(() => {
      const view = this.currentViewDate();
      const start = startOfWeek(startOfMonth(view), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(view), { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    });

    // ✅ Swipe State
    private touchStartX = 0;
    private touchEndX = 0;

    onTouchStart(event: TouchEvent) {
      this.touchStartX = event.changedTouches[0].screenX;
    }

    onTouchEnd(event: TouchEvent) {
      this.touchEndX = event.changedTouches[0].screenX;
      this.handleSwipe();
    }

    private handleSwipe() {
      const SWIPE_THRESHOLD = 50; // Minimum distance to consider a swipe
      if (this.touchEndX < this.touchStartX - SWIPE_THRESHOLD) {
        this.changeMonth(1); // Swipe Left -> Next Month
      }
      if (this.touchEndX > this.touchStartX + SWIPE_THRESHOLD) {
        this.changeMonth(-1); // Swipe Right -> Prev Month
      }
    }

    changeMonth(delta: number) {
      const newDate = delta > 0 ? addMonths(this.currentViewDate(), 1) : subMonths(this.currentViewDate(), 1);
      this.currentViewDate.set(newDate);
      this.monthChange.emit(newDate);
    }

    getBgMonthYear(): string {
      const date = this.currentViewDate();
      const monthEnglish = format(date, 'MMMM');
      const year = format(date, 'yyyy');
      return `${BG_MONTHS[monthEnglish]} ${year}`;
    }

    onDayClick(day: Date) {
      // Note: We allow clicking Red dates if you want to see they are empty, 
      // but usually they are disabled. Here, we disable unless you want "View Only" logic.
      if (this.isDisabled(day)) return;
      this.dateChange.emit(day);
    }

    onTimeSelect(id: number) {
      this.timeChange.emit(id);
    }

    private getBookedIdsForDate(dateStr: string): number[] | null {
      const bookingData = this.bookedDates();
      for (const dto of bookingData) {
        if (!dto.availableDateMap) continue;
        const keys = Object.keys(dto.availableDateMap);
        const matchingKey = keys.find(k => k.startsWith(dateStr));
        if (matchingKey) {
          return dto.availableDateMap[matchingKey];
        }
      }
      return null;
    }

    isDateFullyBooked(date: Date): boolean {
      const dateStr = format(date, 'yyyy-MM-dd');
      const bookedIds = this.getBookedIdsForDate(dateStr);
      if (bookedIds) {
        return bookedIds.length >= this.timeslots().length;
      }
      return false;
    }

    isTimeAvailable(slotId: number): boolean {
      if (!this.selectedDate()) return false;
      const dateStr = this.selectedDate()!;
      const bookedIds = this.getBookedIdsForDate(dateStr);
      if (bookedIds) {
        const isBooked = bookedIds.some(bookedId => Number(bookedId) === Number(slotId));
        return !isBooked;
      }
      return true; 
    }

    isDisabled(day: Date): boolean {
      // 1. Past dates
      if (isBefore(day, this.today)) return true;
      
      // 2. Weekends
      if (isWeekend(day)) return true;

      // 3. Vacations (Fixed Logic)
      const vacationList = this.vacations() || [];
      
      const isVacation = vacationList.some(v => {
        // Handle both casing styles just to be safe, but prioritize your current data structure
        const startStr = v.startDate || v.start_date;
        const endStr = v.endDate || v.end_date;

        if (!startStr || !endStr) return false;

        // Normalize to start of day to ensure correct comparison
        const start = startOfDay(new Date(startStr));
        const end = startOfDay(new Date(endStr));
        
        // Check if the calendar day falls inside the vacation range
        return isWithinInterval(day, { start, end });
      });

      if (isVacation) return true;

      // 4. Fully Booked
      if (this.isDateFullyBooked(day)) return true;

      return false;
    }

    getDayClasses(day: Date): string {
      const isSelected = this.isSelected(day);
      const isToday = isSameDay(day, this.today);
      const isDisabled = this.isDisabled(day);
      const isCurrentMonth = day.getMonth() === this.currentViewDate().getMonth();

      if (isSelected) return 'bg-gradient-to-br from-[#BF7A7A] to-[#8F5F5F] text-white shadow-md font-semibold transform scale-105';
      
      // ✅ Logic update: If it is disabled ONLY because it's full, we still show the red dot (logic in template), 
      // but here we style the text.
      if (isDisabled) return 'text-slate-300 cursor-not-allowed bg-transparent line-through decoration-slate-300';
      
      if (isToday) return 'bg-slate-100 text-[#121219] font-bold border border-slate-200';
      if (!isCurrentMonth) return 'text-slate-300';
      return 'text-[#121219] hover:bg-[#BF7A7A]/10 hover:text-[#BF7A7A] font-medium';
    }

    getAvailabilityDotClass(day: Date): string {
      if (this.isDateFullyBooked(day)) return 'bg-red-400';
      return 'bg-green-400';
    }

    getTimeSlotClass(id: number): string {
      const isSelected = this.selectedTimeslotId() === id;
      const isAvailable = this.isTimeAvailable(id);

      if (isSelected) {
        return 'bg-gradient-to-br from-[#BF7A7A] to-[#8F5F5F] text-white border-transparent shadow-md';
      }

      if (!isAvailable) {
        return 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed opacity-60';
      }

      return 'bg-white text-[#121219] border-slate-200 hover:border-[#BF7A7A] hover:text-[#BF7A7A] hover:shadow-sm';
    }

    isSelected(day: Date): boolean {
      return this.selectedDate() === format(day, 'yyyy-MM-dd');
    }
  }